import { Box, Button, Flex, Grid, GridItem, Text } from '@chakra-ui/react';
import type { FormikProps } from 'formik';
import {
  forwardRef,
  Fragment,
  type ReactNode,
  useImperativeHandle,
  useState,
} from 'react';

import {
  getNestedFormFieldNames,
  scrollToFirstFormField,
} from '../../../helpers/scrollToFormField';
import { ArrowLeftV2Icon } from '../../icons';

export interface MultiStepFormRef {
  handleNext: () => void;
  handleBack: () => void;
  currentStep: number;
}

interface MultiStepFormProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formik: FormikProps<any>;
  steps: { name: string; children: ReactNode }[];
  /** Map of step index → field name(s) to validate before advancing */
  stepFieldMappings?: Record<number, string | string[] | null>;
  controls?: ReactNode;
  onStepChange?: (step: number) => void;
  step?: number;
  setStep?: (step: number) => void;
}

const MultiStepForm = forwardRef<MultiStepFormRef, MultiStepFormProps>(
  (
    { formik, steps, stepFieldMappings, controls, onStepChange, step, setStep },
    ref,
  ) => {
    if (
      (step !== undefined && setStep === undefined) ||
      (step === undefined && setStep !== undefined)
    )
      throw new Error('You must pass both step and setStep or neither');

    const [localStep, setLocalStep] = useState(0);

    const currentStep = step !== undefined ? step : localStep;
    const isLastStep = currentStep === steps.length - 1;

    const advanceTo = (newStep: number) => {
      setStep?.(newStep);
      setLocalStep(newStep);
      onStepChange?.(newStep);
    };

    const handleBack = () => {
      if (currentStep > 0) advanceTo(currentStep - 1);
    };

    const handleNext = async () => {
      const mapping = stepFieldMappings?.[currentStep];

      if (mapping) {
        const fields = Array.isArray(mapping) ? mapping : [mapping];

        const freshErrors = await formik.validateForm();

        let firstInvalidField: string | null = null;

        for (const field of fields) {
          const err = freshErrors[field];
          if (!err) continue;

          if (typeof err === 'object') {
            const nestedTouch = Object.keys(err as object).reduce<
              Record<string, boolean>
            >((acc, key) => ({ ...acc, [key]: true }), {});

            formik.setTouched(
              { ...formik.touched, [field]: nestedTouch },
              false,
            );

            if (!firstInvalidField) {
              firstInvalidField = scrollToFirstFormField(
                getNestedFormFieldNames(
                  field,
                  err as Record<string, unknown>,
                ),
              );
            }
            continue;
          }

          formik.setTouched({ ...formik.touched, [field]: true }, false);

          if (!firstInvalidField) {
            firstInvalidField = scrollToFirstFormField([field]);
          }
        }

        const hasErrors = fields.some((field) => {
          const err = freshErrors[field];
          if (!err) return false;
          if (typeof err === 'object') return Object.keys(err).length > 0;
          return true;
        });
        if (hasErrors) return;
      }

      if (isLastStep) {
        formik.submitForm();
      } else {
        advanceTo(currentStep + 1);
      }
    };

    useImperativeHandle(ref, () => ({
      handleNext,
      handleBack,
      currentStep,
    }));

    return (
      <Grid
        templateColumns={{ base: '1fr', md: '4fr 9fr', lg: '4fr 4fr 4fr' }}
        gap={12}
      >
        <GridItem>
          <Flex direction="column" gap={6} pl={6}>
            <Box>
              {steps.map((s, index) => (
                <Fragment key={s.name}>
                  <Box
                    pl={6}
                    py={2}
                    borderLeft={
                      index === currentStep ? '2px solid' : '1px solid'
                    }
                    borderColor={
                      index === currentStep ? 'richBlack.500' : 'richBlack.50'
                    }
                  >
                    <Text
                      fontSize=".875rem"
                      lineHeight="1.5rem"
                      fontWeight={index === currentStep ? 'medium' : 'normal'}
                      color={
                        index === currentStep
                          ? 'richBlack.500'
                          : index < currentStep
                            ? 'richBlack.400'
                            : 'richBlack.200'
                      }
                    >
                      {s.name}
                    </Text>
                  </Box>
                </Fragment>
              ))}
            </Box>
          </Flex>
        </GridItem>

        <GridItem>
          <Flex direction="column" gap={6}>
            {currentStep > 0 && (
              <Button
                variant="ghost"
                leftIcon={<ArrowLeftV2Icon />}
                onClick={handleBack}
                alignSelf="flex-start"
                px={0}
                _hover={{ background: 'transparent' }}
                fontSize="sm"
                fontWeight="500"
                color="#00111A"
              >
                Back
              </Button>
            )}

            {steps.map(
              (s, index) =>
                index === currentStep && <Box key={index}>{s.children}</Box>,
            )}

            {controls}
          </Flex>
        </GridItem>
      </Grid>
    );
  },
);

MultiStepForm.displayName = 'MultiStepForm';

export default MultiStepForm;
